import { useModal } from '@/contexts/ModalContext'
import { MODAL_TYPES } from '@/libs/config/modal'
import { useCallback, useEffect } from 'react'

const TWO_HOURS_MS = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

export const useReminderOrderTrigger = (storeOrder: Oms.StoreOrder) => {
  const { openModal } = useModal()

  const showReminder = useCallback(() => {
    try {
      openModal(MODAL_TYPES.REMINDER_ORDER, undefined, true)
    } catch (error) {
      console.warn('Failed to show reminder modal:', error)
    }
  }, [openModal])

  const trackOrderActivity = useCallback(() => {
    localStorage.setItem('lastTimeTouchOrder', Date.now().toString())
  }, [])

  const shouldShowReminder = useCallback(() => {
    // Check if user has order data
    if (!storeOrder || !storeOrder.orderItems?.length) {
      return false
    }

    const { isPickup, storeAddress, deliveryAddress, defaultStoreId } =
      storeOrder || {}

    const isAppointmentReady =
      (isPickup && !!storeAddress && !defaultStoreId) ||
      (!isPickup && !!deliveryAddress)

    if (!isAppointmentReady) return false

    // Check last time order was touched
    const lastTimeTouchOrder = localStorage.getItem('lastTimeTouchOrder')
    if (!lastTimeTouchOrder) {
      return false
    }

    const now = Date.now()
    const timeSinceLastTouch = now - Number.parseInt(lastTimeTouchOrder)

    // Show if more than 2 hours have passed
    return timeSinceLastTouch >= TWO_HOURS_MS
  }, [storeOrder])

  // Check on mount and show reminder if needed
  // Only trigger after storeOrderQuery is finished (if provided)
  useEffect(() => {
    // Only run on client side and when modal context is available
    if (typeof window === 'undefined' || !openModal) return

    // If storeOrderQuery is provided, wait for it to finish
    if (storeOrder?.id && shouldShowReminder()) {
      // Add 2-second delay before showing reminder
      const timeoutId = setTimeout(() => {
        showReminder()
      }, 2000)

      // Cleanup timeout on unmount or dependency change
      return () => clearTimeout(timeoutId)
    }
  }, [shouldShowReminder, showReminder, storeOrder?.id, openModal])

  return {
    showReminder,
    trackOrderActivity,
    shouldShowReminder,
  }
}

export default useReminderOrderTrigger
